import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCheck,
  CircleCheckBig,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PendingSignaturesModal } from '@/components/modals/pending-signatures-modal';
import { usePendingSignaturesState } from '@/hooks/admin/member/use-pending-signatures-state';
import { useProposalExecutionState } from '@/components/execution/use-proposal-execution-state';
import {
  DisasterRecoveryProposalAction,
  ExecuteAction,
  getInvalidProposalDisplayMessage,
  OrgOperationDecision,
} from '@mls-wallet/shared-types';
import { SECONDS_PER_DAY } from '@/lib/utils';
import { useActiveDRProposal } from '@/hooks/admin/organization/use-active-disaster-recovery-proposal';
import { useDisasterRecoveryProposalSignature } from '@/hooks/admin/organization/use-disaster-recovery-proposal-signature';
import { useCreateDRProposal } from '@/hooks/admin/organization/use-create-disaster-recovery-proposal';
import { useMyOrganization } from '@/hooks/organization/use-my-organization';
import { useDisasterRecoverySettings } from '@/hooks/admin/organization/use-disaster-recovery-settings';
import {
  useExecuteDRProposal,
  type DRProposalType,
} from '@/hooks/admin/organization/use-execute-dr-proposal';
import type { ActiveDisasterRecoveryProposalResponse } from '@/hooks/admin/organization/use-active-disaster-recovery-proposal';
import {
  canExecuteRejection,
  getOrgProposalSignatureCountsAndUserVote,
} from '@/utils/SignatureCountsHelpers';
import type {
  ActiveDRSupportingProposal,
  DRConfig,
} from '@/hooks/admin/organization/use-disaster-recovery-settings';
import { NestedFullscreenDialog } from '@/components/ui/nested-fullscreen-dialog';
import { ProposalActionButtons } from '@/components/ui/proposal-action-buttons';
import { useAuthClient } from '@/lib/authClient';
import { formatName } from '@/lib/name';
import { DRBadge } from '@/pages/settings/components/disaster-recovery-ui-utils';
import { DRSuccessDialog } from '@/pages/settings/components/disaster-recovery-dialogs';
import type { Signature } from '@/types/signature';
import type { ServerDisasterRecoveryProposalsResponse } from '@/hooks/admin/organization/use-disaster-recovery-proposal-list';
import { SIGNABLE_POLL_INTERVAL_MS } from '@/components/execution/execution-polling';

type PendingSignable = {
  id: string;
  approvals: Signature[];
  rejections: Signature[];
  requiredApprovals: number;
  initiatorSignature: undefined;
};

function getPrimaryActionErrorMessage(err: unknown): string {
  if (err instanceof AxiosError) {
    const cause = err.response?.data?.error?.body?.cause;
    if (typeof cause === 'string' && cause.length > 0) {
      return cause;
    }
  }
  if (err instanceof Error && err.message.length > 0) {
    return err.message;
  }
  return 'Failed to create or sign proposal. Please try again.';
}

// ---------------------------------------------------------------------------
// Variant configuration
// ---------------------------------------------------------------------------

/**
 * Controls which copy, banners, and action buttons are shown.
 *
 *   initiation-review    – reviewing initiation proposal (Approve/Reject)
 *   cancellation-create  – creating a new cancellation proposal (Create button only)
 *   cancellation-review  – reviewing an existing cancellation proposal
 *   finalization-create  – creating a new finalization proposal (Create button only)
 *   finalization-review  – reviewing an existing finalization proposal
 */
export type DRProposalDialogVariant =
  | 'initiation-review'
  | 'cancellation-create'
  | 'cancellation-review'
  | 'finalization-create'
  | 'finalization-review';

interface VariantConfig {
  title: string;
  warningBanner: string | null;
  proposalType: DRProposalType;
  /** Shown on the green/primary action button */
  primaryLabel: string;
  /** Shown on the red/reject button (null = no reject button) */
  rejectLabel: string | null;
  /** Whether this variant creates a new proposal vs reviewing an existing one */
  isCreate: boolean;
  /** Success dialog title after signing */
  signSuccessTitle: string;
  /** Success dialog body after signing */
  signSuccessBody: string;
  /** Success dialog title after execute (null if execute not supported) */
  executeSuccessTitle?: string;
  executeSuccessBody?: string;
  /** Optional suffix for ProposalActionButtons review/execute labels */
  additionalButtonsText?: string;
}

const VARIANT_CONFIGS: Record<DRProposalDialogVariant, VariantConfig> = {
  'initiation-review': {
    title: 'Proposal to initiate disaster recovery setup',
    warningBanner: null,
    proposalType: 'initiation',
    primaryLabel: 'Approve',
    rejectLabel: 'Reject',
    isCreate: false,
    signSuccessTitle: "You've successfully approved this operation",
    signSuccessBody:
      'Disaster recovery setup still requires additional admin approvals before it takes effect.',
    executeSuccessTitle: 'Disaster Recovery Setup Initiated!',
    executeSuccessBody:
      'To finish disaster recovery setup, you will need to wait the duration of the timelock.\n\nAfter the timelock duration has passed, you will need to finalize disaster recovery setup.',
  },
  'cancellation-create': {
    title: 'Create proposal to cancel disaster recovery setup',
    warningBanner:
      'This proposal will cancel disaster recovery setup for the configuration below',
    proposalType: 'cancellation',
    primaryLabel: 'Create cancellation proposal',
    rejectLabel: null,
    isCreate: true,
    signSuccessTitle:
      'Successfully created proposal to cancel disaster recovery setup',
    signSuccessBody:
      'Additional admin approvals are required before this proposal can be executed and disaster recovery setup is cancelled',
  },
  'cancellation-review': {
    title: 'Review proposal to cancel disaster recovery setup',
    warningBanner:
      'This proposal will cancel disaster recovery setup for the configuration below',
    proposalType: 'cancellation',
    primaryLabel: 'Approve Cancellation',
    rejectLabel: 'Reject Cancellation',
    additionalButtonsText: 'Cancellation',
    isCreate: false,
    signSuccessTitle:
      'Successfully approved proposal to cancel disaster recovery setup',
    signSuccessBody:
      'Additional admin approvals are required before this proposal can be executed and disaster recovery setup is cancelled',
  },
  'finalization-create': {
    title: 'Create proposal to finalize disaster recovery setup',
    warningBanner: null,
    proposalType: 'finalization',
    primaryLabel: 'Create finalization proposal',
    rejectLabel: null,
    isCreate: true,
    signSuccessTitle:
      'Successfully created proposal to finalize disaster recovery setup',
    signSuccessBody:
      'Additional admin approvals are required before this proposal can be executed and disaster recovery setup is finalized.',
    executeSuccessTitle: 'Disaster Recovery Setup Complete!',
    executeSuccessBody:
      'Disaster recovery has been fully configured. The recovery wallet can now execute emergency transactions after the timelock duration.',
  },
  'finalization-review': {
    title: 'Proposal to finalize disaster recovery setup',
    warningBanner: null,
    proposalType: 'finalization',
    primaryLabel: 'Approve Finalization',
    rejectLabel: 'Reject Finalization',
    additionalButtonsText: 'Finalization',
    isCreate: false,
    signSuccessTitle:
      'Successfully approved proposal to finalize disaster recovery setup',
    signSuccessBody:
      'Additional admin approvals are required before this proposal can be executed and disaster recovery setup is finalized.',
    executeSuccessTitle: 'Disaster Recovery Setup Complete!',
    executeSuccessBody: 'Disaster recovery has been fully configured.',
  },
};

function formatRemainingApprovalsCount(requiredApprovals: number): string {
  const remainingApprovals = Math.max(requiredApprovals - 1, 0);
  return `${remainingApprovals} more admin${remainingApprovals === 1 ? '' : 's'}`;
}

function getDynamicSignSuccessBody(
  proposalType: DRProposalType,
  requiredApprovals: number
): string {
  const remainingApprovals = Math.max(requiredApprovals - 1, 0);
  if (remainingApprovals === 0) {
    if (proposalType === 'initiation') {
      return 'No additional admin approvals are required before disaster recovery setup takes effect.';
    }
    const noun = proposalType === 'cancellation' ? 'cancelled' : 'finalized';
    return `No additional admin approvals are required before this proposal can be executed and disaster recovery setup is ${noun}.`;
  }

  if (proposalType === 'initiation') {
    return `Disaster recovery setup still requires approval from ${remainingApprovals} other admin${remainingApprovals === 1 ? '' : 's'} before it takes effect.`;
  }

  const noun = proposalType === 'cancellation' ? 'cancelled' : 'finalized';
  return `Approvals from ${formatRemainingApprovalsCount(requiredApprovals)} are required before this proposal can be executed and disaster recovery setup is ${noun}.`;
}

function getProposalFromSettingsResponse(
  proposalType: DRProposalType,
  response: ServerDisasterRecoveryProposalsResponse | undefined
) {
  if (!response) return null;
  if (proposalType === 'initiation') return response.drInitiationProposal;
  if (proposalType === 'cancellation') return response.drCancellationProposal;
  return response.drFinalizationProposal;
}

// ---------------------------------------------------------------------------
// Metadata bar
// ---------------------------------------------------------------------------

function MetaItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-1.5 items-end shrink-0">
      <span className="text-xs text-zinc-500 leading-5 whitespace-nowrap">
        {label}
      </span>
      <span className="text-sm font-medium text-black leading-5 whitespace-nowrap">
        {value}
      </span>
    </div>
  );
}

function MetaDivider() {
  return <div className="bg-zinc-200 w-px h-[20px] shrink-0" />;
}

// ---------------------------------------------------------------------------
// Read-only pill
// ---------------------------------------------------------------------------

function ReadonlyPill({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-zinc-50 border border-white rounded-full shadow-sm p-1.5 w-full">
      <div className="bg-white border border-zinc-100 rounded-full flex items-center px-10 py-4 w-full">
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Success dialog (parameterised)
// ---------------------------------------------------------------------------

interface DRProposalSuccessDialogProps {
  open: boolean;
  title: string;
  body: string;
  onReturnToSettings: () => void;
}

function DRProposalSuccessDialog({
  open,
  title,
  body,
  onReturnToSettings,
}: DRProposalSuccessDialogProps) {
  if (!open) return null;
  // Rendered inside the portal — uses a simple overlay
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-lg w-[430px] flex flex-col overflow-hidden">
        <div className="flex flex-col gap-5 items-center px-8 py-5">
          <p className="text-zinc-700 text-base font-semibold text-center leading-normal">
            {title}
          </p>
          <p className="text-sm text-zinc-600 text-center leading-snug whitespace-pre-line">
            {body}
          </p>
        </div>
        <div className="flex items-center justify-center px-8 pb-5">
          <Button
            onClick={onReturnToSettings}
            className="flex items-center gap-2"
          >
            Return to settings
            <span className="ml-1">→</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main dialog props
// ---------------------------------------------------------------------------

interface DRProposalReviewDialogProps {
  open: boolean;
  onClose: () => void;
  onReturnToSettings: () => void;
  /** Optional callback for initiation-review flows that should surface setup success UI. */
  onSignedSuccess?: () => void;
  variant: DRProposalDialogVariant;
  /** DR config details to display in the dialog body */
  drConfig:
    | (Pick<DRConfig, 'recoveryWalletAddress' | 'timelockDurationSeconds'> & {
        /** Required when creating cancel/finalize proposals */
        id?: string;
      })
    | null;
  /**
   * Existing proposal to review. Required for all non-create variants and
   * when the proposal needs to be signed/executed.
   * For create variants, pass null on first open (dialog will create it).
   */
  existingProposal:
    | ActiveDisasterRecoveryProposalResponse
    | ActiveDRSupportingProposal;
}

/**
 * Generic near-fullscreen dialog covering all five DR proposal interactions:
 *   - Reviewing and signing initiation proposals
 *   - Creating and reviewing cancellation proposals
 *   - Creating and reviewing finalization proposals
 *
 * When a proposal has enough completed signatures (numApprovals >= requiredApprovals),
 * an Execute button appears. Clicking Execute starts app-level execution
 * tracking and shows a success dialog on completion.
 *
 * Rendered using the shared NestedFullscreenDialog scaffold.
 */
export function DRProposalReviewDialog({
  open,
  onClose,
  onReturnToSettings,
  onSignedSuccess,
  variant,
  drConfig,
  existingProposal,
}: DRProposalReviewDialogProps) {
  // Snapshot variant when the dialog opens so create flows do not flip to review
  // after the proposal is created and SWR data refreshes.
  const [lockedVariant, setLockedVariant] = useState(variant);
  useEffect(() => {
    if (open) {
      setLockedVariant(variant);
    }
  }, [open, variant]);

  const cfg = VARIANT_CONFIGS[lockedVariant];
  const { user: currentUser } = useAuthClient();
  const { organization } = useMyOrganization();
  const currentUserId = currentUser?.id ?? null;
  const orgRequiredApprovals = organization?.numberOfApprovalsNeeded ?? 3;

  const [showSignSuccess, setShowSignSuccess] = useState(false);
  const [lastCompletionAction, setLastCompletionAction] = useState<
    'sign' | 'execute-approve' | null
  >(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isPrimaryActionLoading, setIsPrimaryActionLoading] = useState(false);
  // Snapshot signable state immediately after create+sign so the pending modal
  // can render before the finalize/cancel SWR poll returns the new proposal.
  const [pendingPolledOverride, setPendingPolledOverride] = useState<
    PendingSignable[] | null
  >(null);

  const handleDialogClose = () => {
    setShowSignSuccess(false);
    setLastCompletionAction(null);
    setActionError(null);
    setPendingPolledOverride(null);
    onClose();
  };

  const { signProposal: signDRProposal } =
    useDisasterRecoveryProposalSignature();
  const { createProposal: createCancellation } = useCreateDRProposal(
    DisasterRecoveryProposalAction.CANCEL_INITIALIZE_TX_RECOVERY
  );
  const { createProposal: createFinalization } = useCreateDRProposal(
    DisasterRecoveryProposalAction.FINALIZE_INITIALIZE_TX_RECOVERY
  );
  const { executeProposal, isLoading: isExecuteLoading } =
    useExecuteDRProposal();
  const {
    pendingSignableIds,
    setPendingSignableIds,
    pendingDecision,
    setPendingDecision,
    isPendingModalOpen,
    setIsPendingModalOpen,
    resetPendingState,
  } = usePendingSignaturesState();

  const proposalRefreshInterval = open ? SIGNABLE_POLL_INTERVAL_MS : 0;

  // Polling hooks used by PendingSignaturesModal
  const { activeProposal: polledInitiation, refresh: refreshInitiation } =
    useActiveDRProposal(
      DisasterRecoveryProposalAction.INITIATE_INITIALIZE_TX_RECOVERY,
      { refreshInterval: proposalRefreshInterval }
    );
  const { activeProposal: polledCancellation, refresh: refreshCancellation } =
    useActiveDRProposal(
      DisasterRecoveryProposalAction.CANCEL_INITIALIZE_TX_RECOVERY,
      { refreshInterval: proposalRefreshInterval }
    );
  const { activeProposal: polledFinalization, refresh: refreshFinalization } =
    useActiveDRProposal(
      DisasterRecoveryProposalAction.FINALIZE_INITIALIZE_TX_RECOVERY,
      { refreshInterval: proposalRefreshInterval }
    );
  const { refresh: refreshDRConfig } = useDisasterRecoverySettings({
    view: 'config',
    swrOptions: { refreshInterval: 0 },
  });

  const refreshAllDRData = async () => {
    await Promise.all([
      refreshInitiation(),
      refreshCancellation(),
      refreshFinalization(),
      refreshDRConfig(),
    ]);
  };

  // ---------------------------------------------------------------------------
  // Resolve the current proposal and its data
  // ---------------------------------------------------------------------------

  const proposal =
    existingProposal &&
    'proposal' in existingProposal &&
    existingProposal.proposal
      ? existingProposal.proposal
      : null;
  const { isExecuting: isProposalExecuting, startExecution } =
    useProposalExecutionState({
      proposalId: proposal?.id ?? null,
      isServerExecuting:
        existingProposal && 'isExecuting' in existingProposal
          ? existingProposal.isExecuting
          : false,
      isRequestPending: isExecuteLoading,
    });

  const approvals =
    existingProposal && 'approvals' in existingProposal
      ? existingProposal.approvals
      : [];
  const rejections =
    existingProposal && 'rejections' in existingProposal
      ? existingProposal.rejections
      : [];
  const requiredApprovals =
    existingProposal && 'requiredApprovals' in existingProposal
      ? existingProposal.requiredApprovals
      : 3;

  const { numApprovals, numRejections, hasApproved, hasRejected } =
    getOrgProposalSignatureCountsAndUserVote({
      approvals,
      rejections,
      userId: currentUserId ?? undefined,
    });

  const canExecuteApprove =
    proposal?.status === 'PENDING' &&
    numApprovals >= requiredApprovals &&
    numApprovals > 0;
  const canExecuteRejectionProposal = canExecuteRejection({
    status: proposal?.status,
    numRejections,
    requiredApprovals,
  });

  const showMetadataBar = !cfg.isCreate && proposal !== null;

  // ---------------------------------------------------------------------------
  // Polling signable list per variant
  // ---------------------------------------------------------------------------

  const getPolledSignables = (): PendingSignable[] => {
    const polled =
      cfg.proposalType === 'initiation'
        ? polledInitiation
        : cfg.proposalType === 'cancellation'
          ? polledCancellation
          : polledFinalization;

    // Prefer live SWR data whenever it is available so the pending modal can
    // observe signature transitions from null -> signed.
    if (polled?.proposal) {
      return [
        {
          id: polled.proposal.id,
          approvals: polled.approvals,
          rejections: polled.rejections,
          requiredApprovals: polled.requiredApprovals,
          initiatorSignature: undefined,
        },
      ];
    }

    // For create flows, temporarily seed with mutation response until polling
    // returns a proposal row for the newly-created proposal.
    if (pendingPolledOverride) {
      return pendingPolledOverride;
    }

    // Fall back to the proposal snapshot passed from settings so pending modal
    // can still detect cancellation/confirmation edges before first poll tick.
    const source =
      existingProposal && 'proposal' in existingProposal
        ? existingProposal
        : null;
    if (!source?.proposal) return [];
    return [
      {
        id: source.proposal.id,
        approvals: source.approvals,
        rejections: source.rejections,
        requiredApprovals: source.requiredApprovals,
        initiatorSignature: undefined,
      },
    ];
  };

  const getRefresh = () => {
    if (cfg.proposalType === 'initiation') return refreshInitiation;
    if (cfg.proposalType === 'cancellation') return refreshCancellation;
    return refreshFinalization;
  };

  // ---------------------------------------------------------------------------
  // Primary action (sign or create-and-sign)
  // ---------------------------------------------------------------------------

  const handlePrimaryAction = async () => {
    setActionError(null);
    setIsPrimaryActionLoading(true);
    try {
      let proposalId: string;

      if (cfg.isCreate) {
        if (!drConfig?.id) {
          throw new Error(
            'Disaster recovery id is required to create a cancellation or finalization proposal'
          );
        }
        // Case: creating a new proposal — create it, then sign it
        const created =
          cfg.proposalType === 'cancellation'
            ? await createCancellation({ disasterRecoveryId: drConfig.id })
            : await createFinalization({ disasterRecoveryId: drConfig.id });
        if (!created) throw new Error('Failed to create proposal');
        proposalId = created.id;
      } else {
        if (!proposal) throw new Error('No proposal to sign');
        proposalId = proposal.id;
      }

      // Sign with APPROVE decision (pending modal polls until on-device signing completes)
      const signatures = await signDRProposal({
        proposalId,
        decision: OrgOperationDecision.APPROVE,
      });

      const requiredApprovals =
        existingProposal && 'requiredApprovals' in existingProposal
          ? existingProposal.requiredApprovals
          : orgRequiredApprovals;

      // Seed polled signables from the signature response so the pending modal opens
      // reliably for create flows before the next list poll returns the new proposal.
      setPendingPolledOverride([
        {
          id: proposalId,
          approvals: signatures,
          rejections: [],
          requiredApprovals,
          initiatorSignature: undefined,
        },
      ]);

      // Refresh so PendingSignaturesModal sees the new pending signature record
      // (required to detect mobile cancel vs confirm — same pattern as other proposal dialogs).
      await getRefresh()();

      setPendingSignableIds([proposalId]);
      setPendingDecision(OrgOperationDecision.APPROVE);
      setIsPendingModalOpen(true);
    } catch (err) {
      console.error('DR proposal primary action failed:', err);
      setPendingPolledOverride(null);
      setActionError(getPrimaryActionErrorMessage(err));
    } finally {
      setIsPrimaryActionLoading(false);
    }
  };

  const handleRejectAction = async () => {
    if (!proposal) return;
    setActionError(null);
    try {
      const signatures = await signDRProposal({
        proposalId: proposal.id,
        decision: OrgOperationDecision.REJECT,
      });

      setPendingPolledOverride([
        {
          id: proposal.id,
          approvals: [],
          rejections: signatures,
          requiredApprovals,
          initiatorSignature: undefined,
        },
      ]);

      await getRefresh()();

      setPendingSignableIds([proposal.id]);
      setPendingDecision(OrgOperationDecision.REJECT);
      setIsPendingModalOpen(true);
    } catch (err) {
      console.error('DR proposal reject action failed:', err);
      setPendingPolledOverride(null);
      setActionError(getPrimaryActionErrorMessage(err));
    }
  };

  const handleExecute = () => {
    if (!proposal) return;
    // Case: active approval execution blocks a duplicate Guardian request.
    if (isProposalExecuting) return;
    if (!canExecuteApprove) return;
    startExecution({
      executionTarget: 'proposal',
      executionTargetId: proposal.id,
      proposalType: 'disaster recovery',
      request: () =>
        executeProposal({
          proposalId: proposal.id,
          action: ExecuteAction.APPROVE,
        }),
      onAlreadyExecuting: async () => {
        await refreshAllDRData();
      },
      onSuccess: async () => {
        await refreshAllDRData();
        setLastCompletionAction('execute-approve');
        setShowSignSuccess(true);
      },
    });
  };

  const handleExecuteRejection = () => {
    if (!proposal) return;
    // Case: active rejection execution blocks a duplicate Guardian request.
    if (isProposalExecuting) return;
    if (!canExecuteRejectionProposal) return;
    startExecution({
      executionTarget: 'proposal',
      executionTargetId: proposal.id,
      proposalType: 'disaster recovery',
      request: () =>
        executeProposal({
          proposalId: proposal.id,
          action: ExecuteAction.REJECT,
        }),
      onAlreadyExecuting: async () => {
        await refreshAllDRData();
      },
      onSuccess: async () => {
        await refreshAllDRData();
        handleDialogClose();
      },
    });
  };

  const handleSigningComplete = async (result?: 'signed' | 'cancelled') => {
    const pendingProposalId = pendingSignableIds[0];
    const pendingDecisionAtCompletion = pendingDecision;
    resetPendingState();
    setPendingPolledOverride(null);
    const refreshedCurrentProposal = await getRefresh()();
    await refreshAllDRData();

    const refreshedProposal = getProposalFromSettingsResponse(
      cfg.proposalType,
      refreshedCurrentProposal
    );
    const decisionSignatures =
      pendingDecisionAtCompletion === OrgOperationDecision.REJECT
        ? (refreshedProposal?.rejections ?? [])
        : (refreshedProposal?.approvals ?? []);
    const refreshedUserSignatures = currentUserId
      ? decisionSignatures.filter(
          (sig: Signature) => sig.reviewingUserId === currentUserId
        )
      : [];
    const userSignedAfterRefresh =
      refreshedProposal?.proposal?.id === pendingProposalId
        ? refreshedUserSignatures.some((sig: Signature) =>
            Boolean(sig.signature)
          )
        : refreshedUserSignatures.some((sig: Signature) =>
            Boolean(sig.signature)
          );

    const didSign = result === 'signed' || userSignedAfterRefresh;

    // Only show success after a confirmed mobile signature — not on cancel or dialog dismiss.
    if (!didSign) {
      return;
    }
    // Signed rejections should return the reviewer to the proposal screen so they
    // can keep tracking proposal state; do not show DR success for reject flows.
    if (pendingDecisionAtCompletion === OrgOperationDecision.REJECT) {
      return;
    }
    // In initiation-review launched from setup education flow, hand off to setup success
    // modal instead of showing the generic proposal success modal.
    if (lockedVariant === 'initiation-review' && onSignedSuccess) {
      onSignedSuccess();
      handleDialogClose();
      return;
    }
    setLastCompletionAction('sign');
    setShowSignSuccess(true);
  };

  // ---------------------------------------------------------------------------
  // Footer buttons
  // ---------------------------------------------------------------------------

  const renderFooterButtons = () => {
    if (cfg.isCreate) {
      return (
        <div className="flex flex-col items-center gap-2">
          {actionError ? (
            <p className="text-sm text-rose-600 text-center max-w-md">
              {actionError}
            </p>
          ) : null}
          <Button
            type="button"
            className="h-[50px] px-5 bg-green-700 hover:bg-green-800 border border-lime-300 text-yellow-100 gap-2 rounded-lg"
            onClick={() => void handlePrimaryAction()}
            disabled={isPrimaryActionLoading}
          >
            {lockedVariant === 'cancellation-create' && (
              <CircleCheckBig className="w-5 h-5 mr-1" />
            )}
            {isPrimaryActionLoading ? 'Creating proposal…' : cfg.primaryLabel}
          </Button>
        </div>
      );
    }

    return (
      <ProposalActionButtons
        onReject={handleRejectAction}
        onApprove={handlePrimaryAction}
        onExecuteRejection={handleExecuteRejection}
        onExecute={handleExecute}
        hasApproved={hasApproved}
        hasRejected={hasRejected}
        canExecuteRejection={Boolean(canExecuteRejectionProposal)}
        canExecute={Boolean(canExecuteApprove)}
        isExecuting={isProposalExecuting}
        disabled={!proposal || proposal.status !== 'PENDING'}
        approveDisabled={Boolean(proposal && proposal.validity !== 'valid')}
        approveDisabledReason={
          proposal?.validity && proposal.validity !== 'valid'
            ? getInvalidProposalDisplayMessage(proposal.domainInvalidReason)
            : undefined
        }
        {...(cfg.additionalButtonsText
          ? { proposalName: cfg.additionalButtonsText }
          : {})}
      />
    );
  };

  const successTitle =
    lastCompletionAction === 'execute-approve' && cfg.executeSuccessTitle
      ? cfg.executeSuccessTitle
      : cfg.signSuccessTitle;
  const successBody =
    lastCompletionAction === 'execute-approve' && cfg.executeSuccessBody
      ? 'Execution completed. Return to the settings page to continue.'
      : lastCompletionAction === 'sign' && cfg.isCreate && cfg.signSuccessBody
        ? cfg.signSuccessBody
        : getDynamicSignSuccessBody(cfg.proposalType, requiredApprovals);
  // Lock closing while success prompt is shown for create flows and execute completion.
  const lockCloseToReturnSettings =
    showSignSuccess &&
    (lastCompletionAction === 'execute-approve' || cfg.isCreate);

  return (
    <>
      <NestedFullscreenDialog
        open={open}
        onOpenChange={(nextOpen) => {
          // Keep the underlying review dialog locked while execute-completion prompt is visible.
          if (!nextOpen && !lockCloseToReturnSettings) handleDialogClose();
        }}
        title={cfg.title}
        noPadding
        headerClassName="border-b border-zinc-200 px-10 py-[7px]"
        titleClassName="font-medium text-xl text-black leading-5 text-center"
        bodyContainerClassName="bg-zinc-100 p-6"
        footer={renderFooterButtons()}
      >
        <div className="flex flex-col gap-6 items-center w-full">
          {/* Proposal status badges (review modes only) */}
          {showMetadataBar && proposal && (
            <div className="flex w-full max-w-[697px] items-center justify-center gap-1.5">
              {canExecuteApprove ? (
                <DRBadge
                  icon={AlertCircle}
                  label="Needs to be executed"
                  color="amber"
                />
              ) : hasApproved ? (
                <DRBadge icon={Check} label="You approved" color="green" />
              ) : hasRejected ? (
                <DRBadge icon={XCircle} label="You rejected" color="red" />
              ) : (
                <DRBadge
                  icon={AlertCircle}
                  label="Needs your approval"
                  color="amber"
                />
              )}
              {hasApproved && canExecuteApprove && (
                <DRBadge icon={Check} label="You approved" color="green" />
              )}
              <DRBadge
                icon={CheckCheck}
                label={`${numApprovals}/${requiredApprovals} Admin approvals`}
                color="green"
              />
              {numRejections > 0 && (
                <DRBadge
                  icon={XCircle}
                  label={`${numRejections}/${requiredApprovals} Rejections`}
                  color="red"
                />
              )}
            </div>
          )}

          {/* Metadata bar (review modes only) */}
          {showMetadataBar && proposal && (
            <div className="bg-white border border-zinc-200 rounded-lg flex items-center px-6 py-3 w-full max-w-[697px]">
              <div className="flex gap-3 items-center h-5 flex-wrap">
                <MetaItem
                  label="Proposed by"
                  value={
                    (proposal.createdByUserName
                      ? formatName(proposal.createdByUserName)
                      : undefined) ??
                    proposal.createdByUserId ??
                    '—'
                  }
                />
                <MetaDivider />
                <MetaItem
                  label="Proposed on"
                  value={new Date(proposal.createdAt).toLocaleString()}
                />
                <MetaDivider />
                <MetaItem
                  label="Approvals"
                  value={`${numApprovals}/${requiredApprovals}`}
                />
                <MetaDivider />
                <MetaItem
                  label="Rejections"
                  value={`${numRejections}/${requiredApprovals}`}
                />
              </div>
            </div>
          )}

          {/* Warning banner (cancellation variants) */}
          {cfg.warningBanner && (
            <div className="bg-orange-50 border border-amber-200 rounded-xl px-3 py-2.5 flex items-center justify-center gap-2 w-fit">
              <AlertTriangle className="w-4 h-4 text-amber-900 opacity-70 shrink-0" />
              <p className="text-xs font-medium text-amber-900">
                {cfg.warningBanner}
              </p>
            </div>
          )}

          {/* Proposal content */}
          <div className="flex flex-col gap-[50px] items-start w-full max-w-[697px] pb-[50px]">
            {/* Recovery Wallet Address */}
            <div className="flex flex-col gap-2.5 w-full">
              <p className="font-medium text-zinc-800 text-xl leading-7">
                Recovery Wallet Address
              </p>
              <p className="text-sm text-zinc-500 leading-normal">
                The recovery wallet will be able to bypass the Guardian and the
                policy engine and execute transactions on behalf of your
                organization.
              </p>
              <div className="p-2.5 w-full">
                <ReadonlyPill>
                  <p className="font-mono text-base text-zinc-500 break-all">
                    {drConfig?.recoveryWalletAddress ?? '—'}
                  </p>
                </ReadonlyPill>
              </div>
            </div>

            {/* Timelock Duration */}
            <div className="flex flex-col gap-2.5 w-full">
              <p className="font-medium text-zinc-800 text-xl leading-7">
                Timelock Duration
              </p>
              <p className="text-sm text-zinc-500 leading-normal">
                The length of time the recovery wallet must wait after enabling
                disaster recovery before it can execute transactions on behalf
                of your organization
              </p>
              <div className="p-2.5 w-full">
                <ReadonlyPill>
                  <div className="flex items-center gap-2.5">
                    <span className="font-medium text-zinc-800 text-base">
                      {drConfig
                        ? Math.round(
                            drConfig.timelockDurationSeconds / SECONDS_PER_DAY
                          )
                        : '—'}{' '}
                      days
                    </span>
                    <span className="text-sm text-zinc-500">
                      Recovery wallet must wait{' '}
                      {drConfig
                        ? Math.round(
                            drConfig.timelockDurationSeconds / SECONDS_PER_DAY
                          )
                        : '—'}{' '}
                      days after initiating recovery to execute transactions
                    </span>
                  </div>
                </ReadonlyPill>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile signing modal — nested so it layers above this fullscreen shell */}
        <PendingSignaturesModal
          open={isPendingModalOpen}
          resetPendingState={handleSigningComplete}
          refreshSignables={getRefresh()}
          pendingSignableIds={pendingSignableIds}
          polledSignables={getPolledSignables()}
          pendingDecision={pendingDecision}
          // Polling is handled by the top-level proposal hooks via refreshInterval.
          hookFunctionToPoll={() => undefined}
          isTransaction={false}
        />
      </NestedFullscreenDialog>

      {/* Post-sign success dialog (align with setup flow messaging) */}
      <DRSuccessDialog
        open={showSignSuccess && lastCompletionAction === 'sign'}
        requiredApprovals={requiredApprovals}
        onReturnToSettings={async () => {
          setShowSignSuccess(false);
          setLastCompletionAction(null);
          await refreshAllDRData();
          onReturnToSettings();
        }}
      />

      {/* Post-execute success dialog */}
      <DRProposalSuccessDialog
        open={showSignSuccess && lastCompletionAction === 'execute-approve'}
        title={successTitle}
        body={successBody}
        onReturnToSettings={async () => {
          setShowSignSuccess(false);
          setLastCompletionAction(null);
          await refreshAllDRData();
          onReturnToSettings();
        }}
      />
    </>
  );
}
